import React, { useEffect, useMemo, useState } from 'react';
import FormSelect from './FormSelect.jsx';

function nodeToText(node) {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (React.isValidElement(node)) {
    if (node.type === 'br') return ' ';
    return nodeToText(node.props.children);
  }
  return '';
}

function collectOptions(children, options = []) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;

    if (child.type === 'option') {
      const label = nodeToText(child.props.children);
      options.push({
        value: child.props.value != null ? String(child.props.value) : label,
        label,
        disabled: Boolean(child.props.disabled),
      });
      return;
    }

    if (child.type === 'optgroup' || child.type === React.Fragment) {
      collectOptions(child.props.children, options);
    }
  });

  return options;
}

export default function UiSelect({
  children,
  value,
  defaultValue,
  onChange,
  className = '',
  disabled = false,
  required = false,
  name,
  ...rest
}) {
  const options = useMemo(() => collectOptions(children), [children]);
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() => (
    defaultValue !== undefined ? defaultValue : (options[0]?.value ?? '')
  ));
  const currentValue = isControlled ? value : internalValue;

  useEffect(() => {
    if (isControlled || !options.length) return;
    const hasCurrentOption = options.some((option) => String(option.value) === String(internalValue));
    if (!hasCurrentOption) {
      setInternalValue(options[0].value);
    }
  }, [internalValue, isControlled, options]);

  const handleChange = (nextValue) => {
    const value = nextValue == null ? '' : String(nextValue);
    if (!isControlled) {
      setInternalValue(value);
    }
    onChange?.({
      target: { value, name },
      currentTarget: { value, name },
      type: 'change',
    });
  };

  return (
    <div className="ui-select">
      <FormSelect
        value={currentValue}
        onChange={handleChange}
        options={options}
        disabled={disabled}
        controlClassName={className}
      />
      <select
        {...rest}
        className="ui-select-native"
        value={currentValue ?? ''}
        onChange={() => {}}
        disabled={disabled}
        required={required}
        name={name}
        tabIndex={-1}
        aria-hidden="true"
      >
        {children}
      </select>
    </div>
  );
}
